import { useEffect, useState } from "react";
import { LoaderCircle, X, UserPlus, MessageSquare } from "lucide-react";
import shopApi from "../../../../api/shopApi";
import { useNotification } from "../../../../hooks/useNotification";
import AddFriendModal from "../AddFriendModal";

export default function UserInfoModal({
  isOpen,
  setIsOpen,
  uid,
  shop,
  user,
  staffs,
  setContacts,
  setSelectedChat,
}) {
  const { addNotification } = useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [isAddFriendOpen, setIsAddFriendOpen] = useState(false);
  const [isSendingMessage, setIsSendingMessage] = useState(false);

  useEffect(() => {
    if (!isOpen || !uid || !shop?._id) return;
    let cancelled = false;
    setIsLoading(true);
    setErrorMsg("");
    setData(null);
    shopApi
      .getZaloUserInfo({ shopId: shop._id, uid })
      .then((res) => {
        if (cancelled) return;
        if (res?.status === 1) {
          setData(res.data);
        } else {
          setData(res?.data ?? null);
          setErrorMsg(res?.message ?? "Không lấy được thông tin");
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setErrorMsg(err?.message ?? "Có lỗi xảy ra");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, uid, shop?._id]);

  const ownerCheck = (contact) => {
    if (!contact) return true;
    const shopRole = user?.shopRoles?.find((sr) => sr.shopId === shop._id);
    const viewableStaffIds = shopRole?.viewMoreStaffIds ?? [];
    if (
      !user?.role?.includes("admin") &&
      user?._id !== shop?.staffId &&
      shopRole?.role !== "admin" &&
      contact?.staffIds?.length > 0 &&
      !contact.staffIds.includes(user._id) &&
      !contact.staffIds.some((sId) => viewableStaffIds.includes(sId))
    ) {
      addNotification(
        `${contact?.name ?? "Liên hệ"} đang do ${
          staffs?.find((s) => s._id === contact?.staffIds[0])?.name ?? "nhân viên khác"
        } quản lý`,
        -1
      );
      return false;
    }
    return true;
  };

  const handleAddFriend = async (contentAddFriend) => {
    const checkExist = await shopApi.getContactByUid(shop._id, uid);
    const contact = checkExist?.data?.[0] ?? null;
    if (checkExist?.status === 1 && contact) {
      if (!ownerCheck(contact)) return;
      if ((contact.staffIds?.length ?? 0) === 0) {
        await shopApi.updateContact({
          _id: contact._id,
          staffIds: [user._id],
        });
      }
    }
    const resAddFriend = await shopApi.addFriend({
      uid,
      contentAddFriend,
      shopId: shop._id,
      zaloId: shop.zaloId,
      staffId: user?._id,
    });
    if (
      resAddFriend?.status !== 1 &&
      resAddFriend?.message !== "Tự động kết bạn"
    ) {
      addNotification(resAddFriend?.message, resAddFriend?.status);
      return;
    }
    setData((prev) => (prev ? { ...prev, isRequested: true } : prev));
    addNotification("Đã gửi lời mời kết bạn", 1);
  };

  const handleSendMessage = async () => {
    try {
      setIsSendingMessage(true);
      const checkExist = await shopApi.getContactByUid(shop._id, uid);
      if (checkExist?.status === 1) {
        if (!ownerCheck(checkExist.data?.[0])) {
          setIsSendingMessage(false);
          return;
        }
      }
      const res = await shopApi.updateProfileInfo({
        shopId: shop._id,
        uid,
        staffId: user._id,
      });
      if (res?.status === 1) {
        const resContacts = await shopApi.getContactByUids(shop._id, [uid], 1);
        if (resContacts?.status === 1 && resContacts.data?.[0]) {
          setContacts((pre) =>
            pre.map((x) => x.uid).includes(uid)
              ? pre
              : [...pre, resContacts.data[0]]
          );
          setSelectedChat(resContacts.data[0]);
          setIsOpen(false);
        }
      } else {
        addNotification(res?.message ?? "Không mở được hội thoại", -1);
      }
    } catch (err) {
      addNotification(err?.message ?? "Có lỗi xảy ra", -1);
    } finally {
      setIsSendingMessage(false);
    }
  };

  if (!isOpen) return null;

  const profile = data?.profile;
  const isSelf = uid && uid === shop?.zaloId;
  const isFriend = !!data?.isFriend;
  const isRequested = !!data?.isRequested;
  const displayName =
    profile?.displayName ?? profile?.zaloName ?? (uid ? `UID ${uid}` : "Người dùng");
  const avatar = profile?.avatar;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white text-black rounded-lg w-full max-w-md p-6">
          <div className="flex justify-between items-center mb-4 pb-2 border-b">
            <h2 className="text-xl font-semibold">Thông tin người dùng</h2>
            <button
              onClick={() => setIsOpen(false)}
              className="text-gray-500 hover:text-gray-700 transition-colors duration-200"
              aria-label="Đóng"
            >
              <X size={20} />
            </button>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <LoaderCircle className="animate-spin text-blue-500" width={28} height={28} />
            </div>
          ) : (
            <>
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 rounded-full bg-gray-200 overflow-hidden flex-shrink-0">
                  {avatar ? (
                    <img
                      src={avatar}
                      alt={displayName}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400 text-xl font-semibold">
                      {displayName.charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="flex flex-col overflow-hidden">
                  <span className="font-semibold text-lg truncate">
                    {displayName}
                  </span>
                  <span className="text-xs text-gray-500">UID: {uid}</span>
                  {profile?.phone && (
                    <span className="text-xs text-gray-500">
                      SĐT: {profile.phone}
                    </span>
                  )}
                  {profile?.fromCache && (
                    <span className="text-[11px] text-gray-400 italic">
                      Dữ liệu từ cache
                    </span>
                  )}
                </div>
              </div>

              {errorMsg && (
                <div className="mb-3 text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                  {errorMsg}
                </div>
              )}

              <div className="flex flex-wrap items-center gap-2 mb-4">
                {isFriend && (
                  <span className="px-3 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                    Đã là bạn bè
                  </span>
                )}
                {!isFriend && isRequested && (
                  <span className="px-3 py-1 bg-yellow-100 text-yellow-700 text-xs rounded-full">
                    Đã gửi lời mời
                  </span>
                )}
                {isSelf && (
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                    Đây là tài khoản của bạn
                  </span>
                )}
              </div>

              {!isSelf && (
                <div className="flex justify-end gap-2">
                  {!isFriend && !isRequested && (
                    <button
                      onClick={() => setIsAddFriendOpen(true)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center gap-1"
                    >
                      <UserPlus size={16} />
                      Kết bạn
                    </button>
                  )}
                  <button
                    onClick={handleSendMessage}
                    disabled={isSendingMessage}
                    className={`px-4 py-2 rounded-md transition-colors flex items-center gap-1 ${
                      isSendingMessage
                        ? "bg-gray-200 text-gray-500"
                        : "bg-gray-200 text-gray-800 hover:bg-gray-300"
                    }`}
                  >
                    {isSendingMessage ? (
                      <LoaderCircle className="animate-spin" width={16} height={16} />
                    ) : (
                      <MessageSquare size={16} />
                    )}
                    Nhắn tin
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <AddFriendModal
        isOpen={isAddFriendOpen}
        setIsOpen={setIsAddFriendOpen}
        shop={shop}
        addFriend={handleAddFriend}
      />
    </>
  );
}
